<template>
  <div>
    <DataTableWrapper
      ref="tableWrapper"
      :initialSortBy="initialSortBy"
      :data-source="{
        type: 'AxisGQL',
        query: require('@/services/gql/types/City/queries/citiesGrid.gql')
      }"
      :headers="$options.headersConfig"
      :filters="{
        id: { control: 'text', modifier: 'contains' },
        title: { control: 'text', modifier: 'contains' },
        slug: { control: 'text', modifier: 'contains' },
        status: {
          control: 'select',
          options: [...$options.statusList],
          dataType: String
        },
        isArchived: {
          control: 'select',
          options: [
            {
              value: true,
              text: 'Да'
            },
            {
              value: false,
              text: 'Нет'
            }
          ],
          dataType: Boolean
        },
        createdAt: { control: 'date-range' },
        updatedAt: { control: 'date-range' },
        translations: {
          control: 'select',
          options: [
            {
              value: 'ru',
              text: 'ru'
            },
            {
              value: 'en',
              text: 'en'
            },
            {
              value: 'ja',
              text: 'ja'
            },
            {
              value: 'zh',
              text: 'zh'
            },
            {
              value: 'ko',
              text: 'ko'
            }
          ]
        }
      }"
      @query-state-updated="getStateQuery"
      :stored-headers="getCurrentViewCols"
      :views-list="getViewList"
      :active-view-key="getActiveViewKey"
      :views-loading="$async.updateMe.$pending"
      @view-changed="updateStoredView"
      @active-view-changed="updateStoredActiveView"
      @remove-view="removeStoredView"
      @data-loaded="onTableDataChange"
    >
      <template v-slot="{ tableProps, tableListeners, filtersProps, filtersListeners }">
        <v-data-table
          fixed-header
          height="80vh"
          :items-per-page="$options.perPage.itemsPerPage"
          :footer-props="{ itemsPerPageOptions: $options.perPage.itemsPerPageOptions }"
          v-bind="tableProps"
          v-on="tableListeners"
          v-model="mixSelectedItems"
          @update:sort-by="updateSortBy"
          @update:sort-desc="sortDesc = $event"
        >
          <template v-for="(h, i) in $options.headersConfig" v-slot:[`header.${h.value}`]>
            <v-tooltip open-delay="350" :key="i" bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ h.text }}</span>
              </template>
              <span>{{ h.description }}</span>
            </v-tooltip>
          </template>
          <template v-slot:[`body.prepend`]="{}">
            <DataTableFilters
              is-grid-selectable
              v-bind="filtersProps"
              v-on="filtersListeners"
            />
          </template>
          <template
            v-for="(h, i) in $options.headersConfig"
            v-slot:[`item.${h.value}`]="{ item }"
          >
            <div :key="i">
              <div v-if="h.type === 'number' && item[h.value] !== null">
                {{ formatAmount(item[h.value]) }}
              </div>
              <div v-else-if="h.type === 'date'">
                <DateTimeCell :value="item[h.value]" />
              </div>
              <div v-else-if="h.value === 'slug'">
                <div>{{ mixCutSlug(item[h.value]) }}</div>
              </div>
              <div v-else-if="h.value === 'copySlug'">
                <a title="Копировать">
                  <v-btn icon @click="mixCopySlugHandler(item)">
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                </a>
              </div>
              <div v-else-if="h.text === 'Заголовок'">
                <router-link
                  title="Редактировать"
                  :to="{
                    name: 'page-city',
                    params: {
                      id: item.id
                    }
                  }"
                  >{{ item[h.value] }}</router-link
                >
              </div>
              <div v-else-if="h.type === 'boolean'">
                <v-icon
                  v-text="'mdi-checkbox-marked-circle'"
                  :color="item[h.value] ? 'success' : 'grey'"
                />
              </div>
              <div v-else-if="h.type === 'actions'">
                <router-link
                  :to="{
                    name: 'page-city',
                    params: {
                      id: item.id
                    }
                  }"
                  title="Редактировать"
                  ><v-btn icon><v-icon>mdi-pencil</v-icon></v-btn></router-link
                >
                <a title="Удалить">
                  <v-btn icon @click="deleteCity(item.id)">
                    <v-icon color="red">mdi-trash-can-outline</v-icon>
                  </v-btn>
                </a>
              </div>
              <div v-else-if="h.value === 'translations'">{{ getLanguage(item) }}</div>
              <div v-else-if="h.value === 'status'">{{ mixGetStatus(item) }}</div>
              <div v-else>
                {{ get(item, h.value) }}
              </div>
            </div>
          </template>
        </v-data-table>
      </template>
    </DataTableWrapper>
    <ConfirmationDialog ref="confirmCityRemoving" confirmText="Удалить" />
  </div>
</template>

<script>
import { formatAmount, get } from '@/utils'
import perPage from '@/configs/v-data-table.js'
import headersConfig from './headersConfig.js'
import DataTableWrapper from '@/components/data-table-wrapper'
import DataTableFilters from '@/components/data-table-filters'
import DateTimeCell from '@/components/date-time-cell'
import { paramsToListQueryInput } from '@/components/data-table-wrapper/data-sources/axis-gql/utils'
import ConfirmationDialog from '@/components/confirmation-dialog'
import deleteCityMutation from '@/services/gql/types/City/mutations/deleteCity.gql'
import updateMeMutation from '@/services/gql/types/Me/mutations/updateMe.gql'
import { gridDependentMixin } from '@/pages/grid-dependent.mix'

export default {
  name: 'GridCites',
  gridType: 'cites',
  perPage,
  headersConfig,
  mixins: [gridDependentMixin],
  components: {
    DataTableWrapper,
    DataTableFilters,
    DateTimeCell,
    ConfirmationDialog
  },
  data () {
    return {
      queryState: null,
      sortDesc: false,
      initialSortBy: []
    }
  },
  methods: {
    get,
    refetch () {
      this.$refs.tableWrapper.refresh()
    },
    formatAmount,
    getStateQuery (state) {
      this.queryState = paramsToListQueryInput(state)
    },
    async deleteCity (cityId) {
      try {
        await this.$refs.confirmCityRemoving.open()
        await this.$async.deleteCity.$perform({ id: cityId })
        this.$successMsg('Город удален')
        this.refetch()
      } catch (e) {
        if (e.cancel) return
        this.$handleApiError(e.graphQLErrors, `Ошибка при удалении города с ID ${cityId}`)
      }
    },
    updateSortBy ([field]) {
      this.$set(this.initialSortBy, 0, {
        field,
        dir: this.sortDesc[0] ? 'desc' : 'asc'
      })
      this.refetch()
    }
  },
  async: {
    deleteCity (variables) {
      return this.$apollo.mutate({
        mutation: deleteCityMutation,
        variables
      })
    },
    updateMe (variables) {
      return this.$apollo.mutate({
        mutation: updateMeMutation,
        variables
      })
    }
  }
}
</script>
